import { useAuth } from "./AuthContext";
import {
  Plus, Inbox, Megaphone, CalendarClock, Receipt, FileText, ChevronRight
} from "lucide-react";

/**
 * HOME — quick-action hub.
 *
 * Cards are RBAC-aware: if the user can't access the target module they
 * never see the card. Eduardo (isAdmin) sees everything.
 *
 * Group A (operations): visible to managers and field-leads
 *   - New Service Order
 *   - Backlog / Pending clients  (admin sees global; managers see their slice)
 *   - Leads / Sales Pipeline
 *
 * Group B (finance — admin-only):
 *   - Future Revenue
 *   - Expense Tracker
 *   - Invoices Center
 */
export default function HomeModule({ onNavigate }) {
  const { user, hasPermission, isAdmin } = useAuth();

  const cards = [
    // ===== Group A — Operations =====
    {
      key: "new-so",
      group: "ops",
      visible: hasPermission("service_orders", "edit"),
      icon: Plus,
      title: "New Service Order",
      desc: "Quickly schedule a one-time or recurring ticket.",
      go: () => onNavigate?.("service-orders", { openNew: true }),
    },
    {
      key: "backlog",
      group: "ops",
      visible: hasPermission("service_orders", "view"),
      icon: Inbox,
      title: "Backlog / Pending",
      desc: isAdmin
        ? "Global view of unscheduled tickets across all clients."
        : "Unscheduled tickets waiting to be planned.",
      go: () => onNavigate?.("service-orders", { openBacklog: true }),
    },
    {
      key: "leads",
      group: "ops",
      visible: hasPermission("leads", "view") || hasPermission("sales_pipeline", "view"),
      icon: Megaphone,
      title: "Leads & Sales Pipeline",
      desc: "Track new estimates and contracts in progress.",
      go: () => onNavigate?.(
        hasPermission("sales_pipeline", "view") ? "sales-pipeline" : "leads"
      ),
    },
    // ===== Group B — Finance (admin / financial view) =====
    {
      key: "future-revenue",
      group: "finance",
      visible: hasPermission("financial", "view"),
      icon: CalendarClock,
      title: "Future Revenue",
      desc: "12-month projection of receivables, in real-time.",
      go: () => onNavigate?.("financial", { folder: "projected" }),
    },
    {
      key: "expenses",
      group: "finance",
      visible: hasPermission("expense_tracker", "view") || hasPermission("financial", "view"),
      icon: Receipt,
      title: "Expense Tracker",
      desc: "Record and review business expenses.",
      go: () => onNavigate?.("expense-tracker"),
    },
    {
      key: "invoices",
      group: "finance",
      visible: hasPermission("financial", "view"),
      icon: FileText,
      title: "Invoices Center",
      desc: "Issue invoices and send secure pay links to clients.",
      go: () => onNavigate?.("financial", { folder: "cashflow" }),
    },
  ].filter(c => c.visible);

  const groupBadge = {
    ops: { label: "Operations", color: "bg-forest-50 text-forest-700 border-forest-200" },
    finance: { label: "Finance", color: "bg-amber-50 text-amber-700 border-amber-200" },
  };

  return (
    <div data-testid="home-module" className="space-y-6">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold text-stone-800">
          Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""} 👋
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Quick shortcuts to where you spend most of your day.
        </p>
      </div>

      {cards.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-400">
          No shortcuts available for your role yet.
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="home-cards-grid"
        >
          {cards.map(c => {
            const Icon = c.icon;
            const badge = groupBadge[c.group];
            return (
              <button
                key={c.key}
                type="button"
                onClick={c.go}
                data-testid={`home-card-${c.key}`}
                className="group relative text-left bg-white border border-stone-200 rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 hover:border-forest-300 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-500"
              >
                {badge && (
                  <span
                    className={`absolute top-4 right-4 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${badge.color}`}
                  >
                    {badge.label}
                  </span>
                )}
                <div className="w-11 h-11 rounded-xl bg-forest-50 text-forest-700 flex items-center justify-center mb-4 group-hover:bg-forest-100 transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-stone-800 mb-1">{c.title}</h3>
                <p className="text-sm text-stone-500 leading-snug">{c.desc}</p>
                <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-forest-700 opacity-0 group-hover:opacity-100 transition-opacity">
                  Open <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
